// Frontend API configuration & axios client
// Provides a pre-configured axios instance with:
//   - Runtime API prefix detection (dev / prod)
//   - Automatic Bearer token injection
//   - 401 auto-logout
//   - Unified error handling
//
// KEY INSIGHT — why this logic is the way it is:
// Production accesses the backend through nginx, which rewrites paths.
//   Browser → nginx /rwai/api/* → localhost:6002/api/*
//   So the frontend must use prefix /rwai/api (nginx strips /rwai).
//   Browser → nginx /rwai-prod/api/* → localhost:6003/api/*
//   So the frontend must use prefix /rwai-prod/api.
//
// The heuristic uses RUNTIME detection (window.location) so one build
// works for both dev (localhost:3000) and production (nginx proxy).

import axios from 'axios';
import { message } from 'antd';

// ── Runtime prefix detection ──
//
// IMPORTANT: Next.js dev server's async rewrites do NOT forward the
// Authorization header from the browser to the backend, causing all
// authenticated API calls to return 401.  The fix is to detect the dev
// environment and use a direct port-based URL so the browser sends the
// Authorization header directly to the backend (CORS must be configured).
//
// Production goes through nginx which properly forwards headers.
//   Browser → nginx /rwai/api/* → localhost:6002/api/*

const DEV_BACKEND = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:6002';

export const getApiPrefix = () => {
  if (typeof window === 'undefined') return '/rwai/api';
  const host = window.location.hostname;
  const port = window.location.port;
  const path = window.location.pathname;

  // Production via nginx — use path prefix
  if (path.startsWith('/rwai-prod')) return '/rwai-prod/api';
  if (path.startsWith('/rwai')) {
    // Dev mode (localhost:3000 / any port that's not 6002) — bypass rewrite, call backend directly
    // This avoids the Next.js rewrite Authorization header stripping bug.
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${DEV_BACKEND}/api`;
    }
    // 非 localhost 的 /rwai 路径：通过 nginx 代理（如 120.76.250.126/rwai/api → nginx → 6002）
    return '/rwai/api';
  }
  // 域名访问（basePath=""），API 路径为 /api（nginx 代理到生产后端）
  return '/api';
};

export const API_BASE = '';
export const API_PREFIX = getApiPrefix();
export const API_URL = `${API_BASE}${API_PREFIX}`;

// Runtime environment detection
export const getEnv = () => {
  if (typeof window === 'undefined') return 'dev';
  const path = window.location.pathname;
  const host = window.location.hostname;
  // 域名访问（非 IP 且非 localhost）→ 生产环境
  if (host !== 'localhost' && host !== '127.0.0.1' && host !== '120.76.250.126') return 'prod';
  if (path.startsWith('/rwai-prod')) return 'prod';
  return 'dev';
};

/** 统一获取跳转登录页所需的 basePath（生产/开发均可） */
export const getLoginBasePath = (): string => {
  if (typeof window === 'undefined') return '';
  const path = window.location.pathname;
  const m = path.match(/^\/(rwai(?:-prod)?)/);
  if (m) return '/' + m[1];
  // ai.roovjoy.com 域名下 basePath 为空
  const host = window.location.hostname;
  if (host === 'ai.roovjoy.com' || host === 'www.roovjoy.com') return '';
  // fallback：从 pathname 第一段取
  const first = path.split('/').filter(Boolean)[0];
  return first ? '/' + first : '';
};

// ── 401 全局处理：登录过期（会话过期）──────────────────────────────
// 需求：任意接口 401 → 明确中文提示「登录已过期，请重新登录」+ 停止该标签页的
// 轮询/定时请求（含弹窗/页面 poll）+ 引导去登录页；同一会话只提示一次，重新登录后恢复。
// 注意：403 是“无权限”，不等于登录过期，此处不处理（沿用原有「权限不足」提示）。
export const AUTH_EXPIRED_EVENT = 'rwai:auth-expired';

let authExpiredHandled = false;

/** 包装 window.setInterval/setTimeout 登记本标签页的定时器；登录过期时统一清理，
 *  避免「会话已失效，旧标签页仍每 N 秒打后端 / 拉被拒图片」继续堆台账。 */
const _tabTimers = new Set<number>();
const _isNum = (v: unknown): v is number => typeof v === 'number';
function installTimerRegistry() {
  if (typeof window === 'undefined') return;
  const win = window as unknown as Record<string, any>;
  if (win.__rwaiTimerRegistry) return;
  win.__rwaiTimerRegistry = true;
  const oSetInterval = win.setInterval.bind(win);
  const oSetTimeout = win.setTimeout.bind(win);
  const oClearInterval = win.clearInterval.bind(win);
  const oClearTimeout = win.clearTimeout.bind(win);
  win.setInterval = (...a: any[]) => { const id = oSetInterval(...a); if (_isNum(id)) _tabTimers.add(id); return id; };
  win.setTimeout = (...a: any[]) => { const id = oSetTimeout(...a); if (_isNum(id)) _tabTimers.add(id); return id; };
  win.clearInterval = (id?: number) => { if (_isNum(id)) _tabTimers.delete(id); return oClearInterval(id); };
  win.clearTimeout = (id?: number) => { if (_isNum(id)) _tabTimers.delete(id); return oClearTimeout(id); };
}
installTimerRegistry();

/** 停止本标签页登记过的全部定时器（页面/弹窗的轮询与心跳） */
export function stopTabTimers(): void {
  if (typeof window === 'undefined') return;
  const win = window as unknown as Record<string, any>;
  for (const id of Array.from(_tabTimers)) {
    try { win.clearInterval(id); win.clearTimeout(id); } catch { /* ignore */ }
  }
  _tabTimers.clear();
}

/** 登录过期统一入口：同一会话只处理/提示一次；先停轮询 → 再中文提示 → 最后引导去登录页 */
export function handleAuthExpired(): void {
  if (typeof window === 'undefined' || authExpiredHandled) return;   // 并发多个 401 也只会处理一次
  authExpiredHandled = true;
  try { localStorage.removeItem('rwai-auth'); } catch { /* ignore */ }
  stopTabTimers();                                                   // 先停掉该标签页所有轮询/定时请求
  try { window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT)); } catch { /* ignore */ }  // 通知页面/弹窗清理各自的 poll
  message.error({ content: '登录已过期，请重新登录', key: 'rwai-auth-expired', duration: 5 });
  const base = getLoginBasePath();
  window.setTimeout(() => { window.location.href = `${base}/login`; }, 1200);   // 留出提示可见时间再引导登录
}

/** 订阅“登录过期”事件：页面/弹窗注册后停止各自的 poll 定时器 */
export function onAuthExpired(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => { /* noop */ };
  window.addEventListener(AUTH_EXPIRED_EVENT, cb);
  return () => window.removeEventListener(AUTH_EXPIRED_EVENT, cb);
}

/** 重新登录成功 / 新会话开始时调用：恢复 401 处理，允许下一次过期再提示 */
export function resetAuthExpired(): void {
  authExpiredHandled = false;
}

// ── Axios instance ──

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  // NOTE: 不设置默认 Content-Type，让 axios 根据请求数据类型自动设置
  // 如果默认设为 'application/json'，发送 FormData 时不会自动切换为 multipart/form-data
  // 导致 FastAPI 无法解析上传文件 → 422 Validation Error
});

// ── Request interceptor: inject Bearer token ──
apiClient.interceptors.request.use(
  (config) => {
    // Try to get token from localStorage (zustand persist)
    try {
      const raw = localStorage.getItem('rwai-auth');
      if (raw) {
        const parsed = JSON.parse(raw);
        const token = parsed?.state?.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch {
      // ignore parse errors
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: unified error handling ──
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const rawDetail = data?.detail || data?.message || '请求失败';
      // pydantic v2 422 Validation Error: detail is an array of {type, loc, msg, input}
      const toMsg = (d: any): string => {
        if (typeof d === 'string') return d;
        if (Array.isArray(d)) return d.map(e => e?.msg || e?.message || '').filter(Boolean).join('; ') || '请求参数校验失败';
        if (typeof d === 'object' && d !== null) {
          if (d.msg) return d.msg;
          if (d.message) return d.message;
          return JSON.stringify(d);
        }
        return String(d);
      };
      const detail = toMsg(rawDetail);

      if (status === 401) {
        // Don't redirect during login — login API also returns 401 on wrong password
        // Check if the request was to /auth/login by inspecting config.url
        const isLoginRequest = error.config?.url?.includes('/auth/login');
        if (isLoginRequest) {
          // Let the caller handle it (login page shows inline error)
          message.error(detail);
        } else {
          // 会话过期：统一处理（中文提示一次 + 停止轮询/定时器 + 引导去登录页）
          handleAuthExpired();
        }
      } else if (status === 403) {
        message.error('权限不足');
      } else if (status === 429) {
        message.error(detail);
      } else if (status >= 500) {
        message.error('服务器内部错误，请稍后重试');
      } else if (status >= 400) {
        message.error(detail);
      }
    } else if (error.code === 'ECONNABORTED') {
      message.error('请求超时，请检查网络');
    } else if (!error.response) {
      message.error('网络连接失败，请检查网络');
    }
    return Promise.reject(error);
  }
);

export default apiClient;

/**
 * 原生 fetch 封装：自动注入 API 前缀和 Bearer token
 *
 * 所有组件（尤其是新组件）应使用此函数发起 GET 请求，
 * 无需关心 /rwai/api vs /rwai-prod/api，无需手动读取 localStorage。
 * path 示例：'/crm/orders/filter-values?field=xxx'
 */
export const apiFetch = async (path: string, options?: RequestInit): Promise<Response> => {
  const prefix = getApiPrefix();
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };
  if (typeof window !== 'undefined') {
    try {
      const raw = localStorage.getItem('rwai-auth');
      if (raw) {
        const token = JSON.parse(raw)?.state?.token;
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
    } catch { /* ignore */ }
  }
  const res = await fetch(`${prefix}${path}`, { ...options, headers });
  // Token 过期：走统一的登录过期处理（提示 + 停轮询 + 引导登录）；登录请求除外
  if (res.status === 401 && !path.includes('/auth/login')) handleAuthExpired();
  return res;
};

/**
 * Token 过期响应检查。
 * 用于页面中使用原生 fetch 时手动调用：const res = await fetch(...); checkAuthRes(res);
 * 如果响应为 401，自动清除 token 并跳转登录页。
 * 登录请求（/auth/login）不会触发跳转。
 */
export const checkAuthRes = (res: Response, path?: string): void => {
  if (res.status === 401 && (!path || !path.includes('/auth/login'))) {
    handleAuthExpired();
  }
};
